import {
  ButtonTileGroup,
  ButtonTile,
  Button,
  ColorSwatch,
  Heading,
  Pill,
  Price,
  Notification,
  warningIcon,
  SizeTile,
} from "@lululemon/ui-library";
import {
  formatLineBreaks,
  getListPrice,
  getSalePrice,
  isColorAvailableForSelectedSize,
  isSizeAvailableForSelectedColor,
} from "./utils";
import { useQVMContext } from "./hooks";
import styles from "./content-renderer.module.scss";
import LinkGenerator from "@lululemon/ecom-common/src/lib/link-generator";
import { useMemo } from "react";
import qs from "query-string";

/**
 * This component renders the Content of the modal
 * the section displaying the product information and
 * the controls to allow the user add the product
 * to the bag.
 * @returns
 */
const ContentRenderer = () => {
  const {
    error,
    handleAddToBag,
    hideViewDetails,
    isErrorVisible,
    modalError,
    onViewDetails,
    productSummary = {},
    sizes = [],
    skus = [],
    selectedColor,
    selectedColorIndex = 0,
    selectedInventoryDetails,
    selectedSize,
    selectedSizeIdx,
    selectedSku,
    selectedVariant,
    setSelectedColorIndex,
    setSelectedSizeIdx,
    sortedColors = [],
  } = useQVMContext();

  const sku = selectedSku ? selectedSku : selectedVariant;
  const modelDescription = selectedVariant?.imageSet?.modelDescription;
  const productTitle = (
    productSummary?.title || productSummary?.displayName
  )?.split("|")?.[0];
  /**
   * Just to make sure that the view details url contains the
   * color and size selected by the guest.
   */
  const pdpUrl = useMemo(() => {
    if (!productSummary?.pdpUrl) return "";
    const queryStr = qs.stringify({
      color: selectedColor?.code,
      sz: selectedSize,
    });
    return `${productSummary.pdpUrl}?${queryStr}`;
  }, [selectedColor, selectedSize]);

  const withError = isErrorVisible || Boolean(modalError);

  return (
    <div className={styles.contentWrapper}>
      {productTitle && (
        <Heading className={`${styles.title} OneLinkNoTx`}>
          {formatLineBreaks(productTitle, "*")}
        </Heading>
      )}
      <div className={styles.pills}>
        {productSummary?.isFinalSale && <Pill>Final Sale</Pill>}
      </div>
      <div className={styles.price} data-testid="price-component">
        <Price
          currencyCode={sku?.price?.currency.code}
          prices={getListPrice?.({
            selectedSKU: sku,
            skus,
          })}
          salePrices={getSalePrice?.({
            selectedSKU: sku,
            skus,
          })}
        />
      </div>

      <span className={styles.selectedColor}>
        {selectedColor?.name?.replace("no colour", "")}
      </span>
      {/* Colors Swatches */}
      <ButtonTileGroup
        className={styles.colorSwatches}
        selectedIndex={selectedColorIndex}
        onSelectCallback={(colorIndx) => setSelectedColorIndex?.(colorIndx)}
      >
        {sortedColors?.map((color, key) => (
          <ButtonTile
            key={`${key}-${color.code}`}
            isDisabled={
              !isColorAvailableForSelectedSize({
                color,
                selectedSize,
                skus,
              })
            }
            tabIndex="0"
          >
            <ColorSwatch
              id={color.code}
              name={color.name}
              data-testid="color-swatch"
            />
          </ButtonTile>
        ))}
      </ButtonTileGroup>
      {/* Low stock indicator */}
      {selectedInventoryDetails?.isLowStock && (
        <div className={styles.lowStockIndicator}>
          <Notification type="warning" icon={warningIcon} visible>
            {selectedInventoryDetails?.lowStockMessage}
          </Notification>
        </div>
      )}
      {/* Error notification */}
      <div
        className={[styles.notifications, withError ? styles.visible : ""].join(
          " "
        )}
      >
        <Notification
          className={styles.notificationError}
          type="error"
          visible={withError}
        >
          <div>{(error || modalError)?.message}</div>
        </Notification>
      </div>
      {/* Size selection */}
      <div className={styles.selectSizeHeading}>
        {selectedSize ? `Size ${selectedSize}` : "Select Size"}
      </div>
      <ButtonTileGroup
        className={styles.sizeTiles}
        selectedIndex={selectedSizeIdx}
        onSelectCallback={setSelectedSizeIdx}
        key="sizes"
      >
        {sizes?.map((size, key) => (
          <ButtonTile
            isDisabled={
              !isSizeAvailableForSelectedColor({
                size,
                skus,
                selectedColor,
              })
            }
            key={`${key}-${size}`}
            tabIndex="0"
          >
            <SizeTile text={size} data-testid="size-tile" />
          </ButtonTile>
        ))}
      </ButtonTileGroup>
      {/* Model description */}
      {modelDescription && (
        <div className={styles.modelDescription}>{modelDescription}</div>
      )}
      {/* CTAs */}
      <Button
        className={styles.addToBagButton}
        disabled={
          !isColorAvailableForSelectedSize({
            color: selectedColor,
            selectedSize,
            skus,
          }) &&
          !isSizeAvailableForSelectedColor({
            skus,
            size: selectedSize,
            selectedColor,
          })
        }
        variant="filledImpact"
        onClick={() => {
          handleAddToBag();
        }}
        block
      >
        ADD TO BAG
      </Button>
      {!hideViewDetails && (
        <div className={styles.viewProductDetailsContainer}>
          <LinkGenerator
            className={`lll-link-primary ${styles.viewProductDetails}`}
            href={pdpUrl}
            onClick={() => {
              onViewDetails?.({
                product: {
                  name: productTitle,
                },
                selectedColor,
                selectedSize,
                skus,
                selectedSku,
                selectedVariant,
              });
            }}
          >
            View product details
          </LinkGenerator>
        </div>
      )}
    </div>
  );
};

export default ContentRenderer;
